import React from 'react';
import PropTypes from 'prop-types';

import {dispatch, connect} from 'utils/state_manager';

import {Box, Stack, Typography} from 'views/components';
import {HoverToolBox, translate} from 'views/module';

import Message from './Message';

import displayResource from 'utils/miscellaneous/display_resource';
import moment from 'moment';

const MessageFeedBox = (props) => {
  const [toolbarVisible, setToolbarVisible] = React.useState(false);
  const [mouseOver, setMouseOver] = React.useState(false);
  const [editMode, setEditMode] = React.useState(false);
  const [anchorEl, setAnchorEl] = React.useState(null);

  const {
    message,
    userRn,
    jobBackend,
  } = props;

  React.useEffect(() => {
    if (editMode) {
      setToolbarVisible(false);
      return;
    }
    setToolbarVisible(mouseOver);
  }, [editMode, mouseOver]);

  const submittedBy = message.submittedBy;
  const author = displayResource(submittedBy);
  const createdAtMoment = moment(message.createdAt);
  const isToday = createdAtMoment.isSame(moment(), 'day');
  const messageHeader = React.useMemo(() => {
    switch (message.messageType) {
      case 'ANNOUNCEMENTS':
        return {
          title: translate('career.jobDetail.Messages.Feed.Titles.AnnouncementPosted'),
          subTitle: [author].filter(Boolean),
        };
      case 'COMMENTS':
        return {
          title: translate('career.jobDetail.Messages.Feed.Titles.CommentAdded'),
          subTitle: [author].filter(Boolean),
        };
      default:
        return {
          title: translate('career.jobDetail.Messages.Timestamp.PostedOn'),
          subTitle: [author].filter(Boolean),
        };
    }
  }, [author, message.messageType]);
  const timelineLabel = React.useMemo(() => {
    if (!isToday) {
      return {
        primary: createdAtMoment.format('MMM D'),
        secondary: createdAtMoment.format('h:mm A'),
      };
    }

    const diffMinutes = Math.max(moment().diff(createdAtMoment, 'minutes'), 0);
    if (diffMinutes < 1) {
      return {
        primary: translate('career.jobDetail.Messages.Feed.RelativeTime.Today'),
        secondary: translate('career.jobDetail.Messages.Feed.RelativeTime.Now'),
      };
    }

    if (diffMinutes < 60) {
      return {
        primary: translate('career.jobDetail.Messages.Feed.RelativeTime.Today'),
        secondary: translate('career.jobDetail.Messages.Feed.RelativeTime.MinutesAgo', {
          count: diffMinutes,
        }),
      };
    }

    const diffHours = Math.max(moment().diff(createdAtMoment, 'hours'), 1);
    return {
      primary: translate('career.jobDetail.Messages.Feed.RelativeTime.Today'),
      secondary: translate('career.jobDetail.Messages.Feed.RelativeTime.HoursAgo', {
        count: diffHours,
      }),
    };
  }, [createdAtMoment, isToday]);

  const onClickEdit = () => {
    setEditMode(true);
  };

  const onClickEditSave = (event, newMessage, setLoading) => {
    setLoading(true);
    jobBackend.messages.update(message.messageRn, newMessage,
        () => {
          setLoading(false);
          setEditMode(false);
        }, () => {
          setLoading(false);
          setEditMode(false);
        });
  };

  const onClickEditCancel = () => {
    setEditMode(false);
  };

  const onEmojiSelected = (emoji) => {
    const prevEmojiUsers = message?.emoji?.[emoji];
    if (prevEmojiUsers?.find((user) => user === userRn)) {
      jobBackend.messages.removeEmoji(message.messageRn, emoji, userRn);
      return;
    }

    jobBackend.messages.addEmoji(message.messageRn, emoji, userRn);
  };

  const onClickDelete = (setIsDeleting) => {
    setIsDeleting(true);
    jobBackend.messages.delete(
        message.messageRn,
        () => setIsDeleting(false),
        () => setIsDeleting(false),
    );
  };

  return (
    <Box
      onMouseOver={(event) => {
        setMouseOver(true);
        setAnchorEl(event.currentTarget);
      }}
      onMouseLeave={() => setMouseOver(false)}
      sx={{
        borderRadius: '10px',
        backgroundColor: toolbarVisible ? '#F9F8F6' : 'transparent',
        transition: 'background-color 0.15s ease',
      }}
    >
      <Stack
        direction='row'
        spacing={{md: 2, xs: 1.25}}
        alignItems='flex-start'
        sx={{
          py: 0.5,
          px: 0.5,
        }}
      >
        <Stack
          spacing={0.1}
          sx={{
            width: '64px',
            flexShrink: 0,
          }}
        >
          <Typography
            sx={{
              fontSize: '11px',
              lineHeight: '14px',
              color: isToday ? '#D97706' : '#7C7C7C',
            }}
          >
            {timelineLabel.primary}
          </Typography>
          <Typography
            sx={{
              fontSize: '11px',
              lineHeight: '14px',
              color: isToday ? '#D97706' : '#7C7C7C',
            }}
          >
            {timelineLabel.secondary}
          </Typography>
        </Stack>
        <Box
          sx={{
            pt: '4px',
            width: '10px',
            flexShrink: 0,
          }}
        >
          <Box
            sx={{
              width: '10px',
              height: '10px',
              borderRadius: '50%',
              backgroundColor: '#D8D3CC',
              border: '1.5px solid #D8D3CC',
            }}
          />
        </Box>
        <Stack
          spacing={0.75}
          sx={{
            minWidth: 0,
            flex: 1,
            pr: 1,
          }}
        >
          <Typography
            sx={{
              fontSize: '13px',
              lineHeight: '17px',
              fontWeight: 500,
              color: '#1A1916',
            }}
          >
            {messageHeader.title}
          </Typography>
          {
            messageHeader.subTitle.length > 0 &&
            <Stack
              direction='row'
              spacing={0.75}
              sx={{
                flexWrap: 'wrap',
                rowGap: 0.25,
              }}
            >
              {
                messageHeader.subTitle.map((item, index) => {
                  return (
                    <Typography
                      key={`${item}-${index}`}
                      sx={{
                        fontSize: '12px',
                        lineHeight: '19px',
                        color: '#6B6760',
                      }}
                    >
                      {index > 0 ? `${'\u00B7'} ${item}` : item}
                    </Typography>
                  );
                })
              }
            </Stack>
          }
          <Message
            userRn={userRn}
            message={message.body.blob}
            emojis={message.emoji}
            editMode={editMode}
            onClickSave={onClickEditSave}
            onClickCancel={onClickEditCancel}
            onEmojiSelected={onEmojiSelected}
            emojisWithRequesterData={message.emojisWithRequesterData}
            truncate
          />
        </Stack>
      </Stack>
      <HoverToolBox
        toolBar={toolbarVisible}
        anchorEl={anchorEl}
        copiedText={message.body.blob}
        onClickEdit={onClickEdit}
        onClickDelete={onClickDelete}
        onEmojiSelected={onEmojiSelected}
        hideTool={{
          edit: userRn !== message.submittedBy?.resourceRn,
          delete: userRn !== message.submittedBy?.resourceRn,
        }}
      />
    </Box>
  );
};

MessageFeedBox.propTypes = {
  message: PropTypes.object.isRequired,
  jobBackend: PropTypes.object.isRequired,
  userRn: PropTypes.string.isRequired,
};

const mapStateToProps = (state) => ({
  userRn: state.user.userRn,
});

export default connect(mapStateToProps, dispatch)(React.memo(MessageFeedBox));
